/**
 * Point d'entrée du bureau.
 *
 * Trois états possibles au démarrage : reprise de session en cours, anonyme
 * (écran de connexion), connecté (application). Rien d'autre n'est rendu tant
 * que l'état n'est pas tranché — cela évite l'éclair d'écran de connexion sur
 * une session valide.
 */
import { StrictMode, useEffect, useState, type MouseEvent } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { AuthProvider, useAuth } from './auth/AuthContext';
import { sortieAutorisee } from './domaine/garde-modifications';
import { NOM_PRODUIT, useIdentiteEspace } from './domaine/identite-espace';
import { adresseRedirectionPlanning, planningActif } from './domaine/menu-planning';
import { useInstallation, type EtatInstallation } from './domaine/installation-terminee';
import { AssetDetailPage } from './pages/AssetDetailPage';
import { AssetImportPage } from './pages/AssetImportPage';
import { EtiquettesQrPage } from './pages/EtiquettesQrPage';
import { BonImpressionPage } from './pages/BonImpressionPage';
import { GammeImpressionPage } from './pages/GammeImpressionPage';
import { AttributeDefinitionsPage } from './pages/AttributeDefinitionsPage';
import { CompliancePage } from './pages/CompliancePage';
import { DocumentCategoriesPage } from './pages/DocumentCategoriesPage';
import { ParcPage } from './pages/ParcPage';
import { MachinesAValiderPage } from './pages/MachinesAValiderPage';
import { PartyDetailPage } from './pages/PartyDetailPage';
import { DevicesPage } from './pages/DevicesPage';
import { ImportPage } from './pages/ImportPage';
import { InterventionsPage } from './pages/InterventionsPage';
import { FichePreparationPage } from './pages/FichePreparationPage';
import { PlanningInterventionsPage } from './pages/PlanningInterventionsPage';
import { PreparerAnneePage } from './pages/PreparerAnneePage';
import { ReglagesBonsPage } from './pages/ReglagesBonsPage';
import { SuitesPage } from './pages/SuitesPage';
import { InvitationPage } from './pages/InvitationPage';
import { LoginPage } from './pages/LoginPage';
import { MaintenanceProfilesPage } from './pages/MaintenanceProfilesPage';
import { TarifsPage } from './pages/TarifsPage';
import { ConditionsAchatPage } from './pages/ConditionsAchatPage';
import { TarifsMoPage } from './pages/TarifsMoPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { SettingsPage } from './pages/SettingsPage';
import { SiteImportPage } from './pages/SiteImportPage';
import { SitePage } from './pages/SitePage';
import { UsersPage } from './pages/UsersPage';
import './styles.css';

function Navigation({ installation }: { installation: EtatInstallation }) {
  const { state, logout } = useAuth();
  const identite = useIdentiteEspace();
  const location = useLocation();
  const lien = ({ isActive }: { isActive: boolean }) => (isActive ? 'actif' : '');
  // « Planning » reste allumé sur toutes ses pages : son lien ne pointe que vers la première (voir domaine/menu-planning.ts).
  const lienPlanning = () => (planningActif(location.pathname) ? 'actif' : '');

  const installationVisible = installation !== 'terminee';
  const sousMenuActif = location.pathname === '/installation' || location.pathname === '/parametres';
  const [parametresOuvert, setParametresOuvert] = useState(sousMenuActif);

  // Rester ouvert quand on navigue dans le groupe, sans jamais se refermer
  // tout seul quand on le quitte : c'est l'utilisateur qui replie.
  useEffect(() => {
    if (sousMenuActif) setParametresOuvert(true);
  }, [sousMenuActif]);

  /*
   * Un éditeur de gamme ouvert porte du travail non enregistré : un clic sur un lien du menu ou sur
   * « Se déconnecter » demande d'abord confirmation. Posé en CAPTURE sur tout le menu : le
   * `preventDefault` passe avant le gestionnaire du lien, que React Router respecte (il ne navigue
   * pas quand l'événement est déjà annulé).
   */
  const gardeSortie = (e: MouseEvent<HTMLElement>) => {
    const cible = e.target instanceof Element ? e.target : null;
    if (cible?.closest('a[href], .pied-nav button') && !sortieAutorisee()) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <aside className="navigation" onClickCapture={gardeSortie}>
      <div className="marque-nav">
        <strong>
          {identite?.nom ?? NOM_PRODUIT}
          {identite?.environnement && (
            <span className="etiquette-environnement">{identite.environnement}</span>
          )}
        </strong>
        <span>Espace d’administration</span>
      </div>

      <nav>
        <NavLink to="/parc" className={lien}>
          Parc
        </NavLink>
        <NavLink to="/planning/interventions" className={lienPlanning}>
          Planning
        </NavLink>
        <NavLink to="/interventions" className={lien}>
          Interventions
        </NavLink>
        <NavLink to="/suites" className={lien}>
          Suites à traiter
        </NavLink>
        <NavLink to="/utilisateurs" className={lien}>
          Membres
        </NavLink>
        <div className="groupe-nav">
          <div className="groupe-nav-entete">
            <NavLink to="/parametres" className={lien} end>
              Paramètres
            </NavLink>
            {/* L'installation quitte le menu une fois terminée : un assistant
                de démarrage n'a plus de raison d'être visible. Elle reste
                atteignable depuis la page Paramètres elle-même. */}
            {installationVisible && (
              <button
                type="button"
                className="bouton-repli"
                aria-expanded={parametresOuvert}
                aria-label={
                  parametresOuvert
                    ? 'Replier le sous-menu Paramètres'
                    : 'Déplier le sous-menu Paramètres'
                }
                onClick={() => setParametresOuvert((ouvert) => !ouvert)}
              >
                <span aria-hidden="true">{parametresOuvert ? '▾' : '▸'}</span>
              </button>
            )}
          </div>
          {installationVisible && parametresOuvert && (
            <NavLink
              to="/installation"
              className={({ isActive }) => `sous-lien${isActive ? ' actif' : ''}`}
            >
              Installation
            </NavLink>
          )}
        </div>
        <NavLink to="/champs-techniques" className={lien}>
          Champs techniques
        </NavLink>
        <NavLink to="/categories-documents" className={lien}>
          Catégories de documents
        </NavLink>
        <NavLink to="/gammes" className={lien}>
          Gammes d’entretien
        </NavLink>
        <NavLink to="/tarifs" className={lien}>
          Tarifs des fabricants
        </NavLink>
        <NavLink to="/conditions-achat" className={lien}>
          Conditions d’achat
        </NavLink>
        <NavLink to="/tarifs-main-d-oeuvre" className={lien}>
          Tarifs de main d’œuvre
        </NavLink>
        <NavLink to="/conformite" className={lien}>
          Conformité
        </NavLink>
        <NavLink to="/terminaux" className={lien}>
          Terminaux
        </NavLink>
      </nav>

      <div className="pied-nav">
        {state.kind === 'connecte' && <span className="courriel">{state.email}</span>}
        <button className="secondaire discret" onClick={() => void logout()}>
          Se déconnecter
        </button>
      </div>
    </aside>
  );
}

/** L'ancien « /planning » (favoris) mène au planning des interventions et garde sa chaîne de requête (`?semaine=…`). */
function RedirectionPlanning() {
  const { search } = useLocation();
  return <Navigate to={adresseRedirectionPlanning(search)} replace />;
}

function Application() {
  const { state } = useAuth();
  const installation = useInstallation();

  if (state.kind === 'chargement') {
    return <div className="centre-page">Chargement…</div>;
  }

  if (state.kind === 'anonyme') {
    return <LoginPage />;
  }

  /*
   * Page d'accueil : l'installation tant qu'elle n'est pas finie, le parc
   * ensuite — c'est l'écran de travail quotidien. Tant que l'état n'est pas
   * connu, on n'oriente PAS : rediriger puis se raviser ferait clignoter
   * l'application à chaque ouverture.
   */
  const accueil = installation === 'terminee' ? '/parc' : '/installation';

  return (
    <div className="application">
      <Navigation installation={installation} />
      <main className="contenu">
        <Routes>
          <Route path="/installation" element={<OnboardingPage />} />
          <Route path="/parc" element={<ParcPage />} />
          <Route path="/parc/a-valider" element={<MachinesAValiderPage />} />
          <Route path="/planning" element={<RedirectionPlanning />} />
          <Route path="/planning/interventions" element={<PlanningInterventionsPage />} />
          <Route path="/planning/preparation" element={<FichePreparationPage />} />
          <Route path="/planning/annee" element={<PreparerAnneePage />} />
          <Route path="/interventions" element={<InterventionsPage />} />
          <Route path="/interventions/imprimer" element={<BonImpressionPage />} />
          <Route path="/interventions/reglages" element={<ReglagesBonsPage />} />
          <Route path="/suites" element={<SuitesPage />} />
          <Route path="/tiers/:id" element={<PartyDetailPage />} />
          <Route path="/import" element={<ImportPage />} />
          <Route path="/sites/import" element={<SiteImportPage />} />
          <Route path="/sites/:id" element={<SitePage />} />
          <Route path="/machines/import" element={<AssetImportPage />} />
          <Route path="/machines/etiquettes" element={<EtiquettesQrPage />} />
          <Route path="/machines/:id" element={<AssetDetailPage />} />
          <Route path="/utilisateurs" element={<UsersPage />} />
          <Route path="/parametres" element={<SettingsPage />} />
          <Route path="/champs-techniques" element={<AttributeDefinitionsPage />} />
          <Route path="/categories-documents" element={<DocumentCategoriesPage />} />
          <Route path="/gammes" element={<MaintenanceProfilesPage />} />
          <Route path="/gammes/imprimer" element={<GammeImpressionPage />} />
          <Route path="/tarifs" element={<TarifsPage />} />
          <Route path="/conditions-achat" element={<ConditionsAchatPage />} />
          <Route path="/tarifs-main-d-oeuvre" element={<TarifsMoPage />} />
          <Route path="/conformite" element={<CompliancePage />} />
          <Route path="/terminaux" element={<DevicesPage />} />
          <Route
            path="*"
            element={
              installation === 'inconnu' ? (
                <div className="vide">Chargement…</div>
              ) : (
                <Navigate to={accueil} replace />
              )
            }
          />
        </Routes>
      </main>
    </div>
  );
}

/**
 * Aiguillage de plus haut niveau.
 *
 * L'acceptation d'invitation est rendue AVANT tout contrôle de session : l'invité
 * n'a pas encore de compte utilisable, il ne peut donc pas s'authentifier. Placée
 * à l'intérieur d'`Application`, elle renverrait vers l'écran de connexion —
 * celui-là même auquel la personne n'a pas encore accès.
 */
function Racine() {
  return (
    <Routes>
      <Route path="/invitation/:token" element={<InvitationPage />} />
      <Route path="*" element={<Application />} />
    </Routes>
  );
}

const racine = document.getElementById('root');
if (!racine) {
  throw new Error('Élément racine introuvable.');
}

createRoot(racine).render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <Racine />
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
